import type { EntityDetails, HistoryItem } from './api';

export interface HistoryDateGroup {
  /** ISO date key (YYYY-MM-DD) used for stable sorting/keys. */
  dateKey: string;
  /** Human-friendly date label per Figma, e.g. "20 Aug 2026". */
  dateLabel: string;
  /** Entries for this date, newest-first (flat, no client sub-grouping). */
  items: HistoryItem[];
}

const DATE_LABEL_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

/** YYYY-MM-DD key in local time for grouping by calendar date. */
function toDateKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** "20 Aug 2026"-style label per Figma. */
function toDateLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', DATE_LABEL_FORMAT).format(d);
}

/**
 * Groups history items by calendar date and orders them for the timeline:
 * date groups newest → oldest, and a flat newest → oldest list within each
 * group (no client-based sub-grouping). Pure function.
 *
 * Items from GET /history already arrive newest-first, but this does not rely
 * on that — it sorts explicitly by `createdAt` so ordering is deterministic.
 */
export function groupHistoryByDate(items: HistoryItem[]): HistoryDateGroup[] {
  const byKey = new Map<string, HistoryItem[]>();

  for (const item of items) {
    const key = toDateKey(item.createdAt);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(item);
    else byKey.set(key, [item]);
  }

  const groups: HistoryDateGroup[] = [];
  for (const [dateKey, groupItems] of byKey) {
    const sorted = [...groupItems].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    groups.push({
      dateKey,
      dateLabel: toDateLabel(sorted[0].createdAt),
      items: sorted,
    });
  }

  // Date groups newest → oldest.
  groups.sort((a, b) => (a.dateKey < b.dateKey ? 1 : a.dateKey > b.dateKey ? -1 : 0));
  return groups;
}

/**
 * Display name for an entity from its stored snapshot: "First Last",
 * falling back to preferred name, then a generic label. Client id remains
 * the source of truth — this is display only.
 */
export function entityDisplayName(details: EntityDetails | null): string {
  if (!details) return 'Client';
  const full = [details.firstName, details.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (full) return full;
  if (details.preferredName) return details.preferredName;
  return 'Client';
}

/**
 * Formats a client's location as "Suburb, State" from an entity snapshot's
 * `preferredSuburb` / `preferredState`. Either part may be absent: with only
 * one present it renders that part alone; with neither it returns an empty
 * string (the caller omits the segment). Display-only.
 */
export function entityLocation(details: EntityDetails | null): string {
  if (!details) return '';
  return [details.preferredSuburb, details.preferredState]
    .filter(Boolean)
    .join(', ')
    .trim();
}

/**
 * Formats a date-of-birth string as "Date Month Year" (e.g. "23 Feb 1932").
 * Accepts any value `new Date(...)` can parse (ISO date, ISO datetime).
 * Returns the original string unchanged when it cannot be parsed, so a
 * non-standard stored value is never silently dropped.
 */
export function formatBornDate(dob: string): string {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return dob;
  return new Intl.DateTimeFormat('en-GB', DATE_LABEL_FORMAT).format(d);
}

/**
 * Normalises an adviser name to "First Last" order. XPLAN stores the adviser
 * name "Last, First" (e.g. "Rongala, Sravya"); this flips it to "First Last"
 * ("Sravya Rongala") to match the client name convention. Names without a
 * comma are returned unchanged.
 */
export function formatAdviserName(name: string): string {
  const [last, first] = name.split(',').map((p) => p.trim());
  if (first && last) return `${first} ${last}`;
  return name.trim();
}

/**
 * The history entry label: "<FirstName LastName> · <queryText>".
 * Name is display-only (from the snapshot); the entry is keyed by entityId.
 */
export function historyEntryLabel(item: HistoryItem): string {
  return `${entityDisplayName(item.entityDetails)} · ${item.queryText}`;
}
